// Item 406 — the suspicious level on the dashboard. The server decides (the task handler's clue specialists and
// the rule of the camera's group) and writes `level: 'suspicious'` with the clue answers in `clue_results`; the
// dashboard reads only those. An event stays open until someone presses Seen (team-wide, `seen_by` / `seen_at`),
// quietly: no running light, no critical sound, no pass through the mutes. Pure, no React.
import type { ClueResult, Event } from '@/types'

/** The dashboard block looks back 24 hours: an unseen row older than that leaves the block on its own. */
export const SUSPICIOUS_WINDOW_MS = 24 * 3600 * 1000

export function isSuspiciousEvent(event: unknown): boolean {
  return !!event && typeof event === 'object' && (event as { level?: unknown }).level === 'suspicious'
}

/** #413 — an event that deserves the Suspicious toast: Suspicious, nobody has pressed Seen, and not Critical (Critical
 *  wins; an already-seen one is a normal event). Pure, for the toast and (Task 5) the chime. */
export function isSuspiciousAlarm(event: unknown): boolean {
  return isSuspiciousEvent(event) && !(event as { seen_at?: unknown }).seen_at && (event as { is_critical?: unknown }).is_critical !== true
}

/** Suspicious and nobody has pressed Seen yet. */
export function isOpenSuspicious(event: Event): boolean {
  return isSuspiciousEvent(event) && !event.seen_at
}

const isClue = (v: unknown): v is ClueResult =>
  !!v && typeof v === 'object' && typeof (v as ClueResult).code === 'string' && typeof (v as ClueResult).state === 'string'

/** The clue answers that said yes, in the server's order; anything that is not a clue answer is left out. */
function yesClues(event: unknown): ClueResult[] {
  const results = (event as { clue_results?: unknown } | null | undefined)?.clue_results
  if (!Array.isArray(results)) return []
  return results.filter((r): r is ClueResult => isClue(r) && r.state === 'yes')
}

const summaryOf = (r: ClueResult): string | null => {
  const summary = r.evidence && typeof r.evidence === 'object' ? (r.evidence as { summary?: unknown }).summary : undefined
  return typeof summary === 'string' && summary.trim() ? summary : null
}

/** The plain-words summaries of the clues that said yes ("standing still 12 s"), in order; clues without words left out. */
export function clueSummaries(event: unknown): string[] {
  return yesClues(event).flatMap((r) => summaryOf(r) ?? [])
}

/** The clue codes with a translated reason (`common:suspicious.reasons.<code>`): the ones that can say yes. #408 — with
 *  the five appearance clues. */
export const CLUE_REASON_CODES: readonly string[] = ['person', 'spray_sound', 'still', 'staying', 'at_train', 'no_hivis', 'backpack', 'hoodie', 'mask', 'spray_can']

/**
 * The reasons to show for the clues that said yes (the row's label, the card, the popup's chips), in order. The task
 * handler writes `evidence.summary` in English: English readers get those words (they carry the numbers), anyone else
 * the translated reason; each side falls back on the other, and a clue with neither is left out (never a raw code).
 */
export function clueReasons(event: unknown, language: string | undefined, translate: (code: string) => string | null): string[] {
  const english = (language ?? 'en').toLowerCase().startsWith('en')
  return yesClues(event).flatMap((r) => {
    const summary = summaryOf(r)
    const translated = translate(r.code)
    return (english ? summary ?? translated : translated ?? summary) ?? []
  })
}

/** The `start_time` of the dashboard block's query: 24 hours before `now` (ms since the epoch), ISO 8601. */
export function suspiciousSince(now: number): string {
  return new Date(now - SUSPICIOUS_WINDOW_MS).toISOString()
}
